import React, {
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { HeadTag } from 'shared/components';
import FooterWrapper, { innerFooterWrapper } from './FooterWrapper';
import Footer, { inferFooter, ReviewData } from './components/Footer';
import DocumentViewer, {
    inferDocumentViewer,
} from './components/DocumentViewer';
import { useLocation, useNavigate } from 'react-router-dom';

import { ManagePacketService } from 'services/ManagePacketService';
import { Utils } from 'shared/helpers';
import { Formik, FormikProps } from 'formik';
import { SendToReworkScheme } from 'validators/SolicitorViewerScheme';
import { formattingAction } from 'shared/helpers/FormatAction';
import { useLeavePrompt } from 'shared/helpers/UseExitPrompt';
import {
    useDebounce,
    useEvent,
    usePageLeave,
    useTimeoutFn,
    useUpdateEffect,
    useWindowSize,
} from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { AxiosError, CanceledError } from 'axios';
import { SocketActions } from 'state/socket/Action';
import { LOCATION_CHANGE } from 'redux-first-history';
import { Action } from 'history';
import { Box, CircularProgress } from '@mui/material';
import { Vertices } from './definition/vertices';
import { VerticeSystem } from './definition/abstract';

import DateFnsAdapter from '@date-io/date-fns';
import { BroadcastActions } from 'state/broadcast/Action';
import { CarryAppActions } from 'state/carriesapplication/Action';
import { usePromiseRetry } from 'shared/helpers/PromiseRetry';
import { BrowserMeta } from 'BrowserHistory';
import isPointerInside from 'shared/helpers/isPointerInside';

const dateFns = new DateFnsAdapter();

// import { CarryAppActions } from 'state/carriesapplication/Action';

export const DocumentMeta = React.createContext<DocumentMeta | null>(null);

const SolicitorDocumentPage = (): ReactElement => {
    const location = useLocation();
    const navigate = useNavigate();
    const defaultPacketState = location.state as defaultReviewedState;
    const router = useSelector((state: any) => state.router);
    const recordLocations = useSelector(
        (state: any) => state.persistState.recordLocations
    );
    const innerMainRef = useRef<HTMLDivElement | null>(null);
    const browserProps = useContext(BrowserMeta);
    const socketState = useSelector((state: any) => state.socketState);
    const profileState = useSelector((state: any) => state.profileState);
    const dispatch = useDispatch();
    const innerFooterWrapperRef = useRef<innerFooterWrapper | null>(null);
    const formikRef = useRef<null | FormikProps<FormikSolicitorDocumentProps>>(
        null
    );
    const documentViewerRef = useRef<inferDocumentViewer | null>(null);
    const innerFooterRef = useRef<inferFooter | null>(null);
    const [currentOriginalData, setOriginalData] = React.useState<
        ReviewPacketData[] | null
    >(null);
    const [currentStepData, setStepData] =
        React.useState<ReviewPacketData | null>(null);
    const [currentStepNumber, setStepNumber] = React.useState<number | null>(
        null
    );
    const [currentType, setCurrentType] = React.useState<string>('');
    const [isLeftPacket, setLeftAsPacket] = React.useState<boolean>(true);
    const [isRightPacket, setRightAsPacket] = React.useState<boolean>(true);
    const [leftDocument, setLeftDocument] = React.useState<string | null>('');
    const [rightDocument, setRightDocument] = React.useState<string | null>('');
    const [leftCategory, setLeftCategory] = React.useState<string>('');
    const [rightCategory, setRightCategory] = React.useState<string>('');
    const [leftFilename, setLeftFilename] = React.useState<string>('');
    const [rightFilename, setRightFilename] = React.useState<string>('');
    const [leftVerticesData, setLeftVertices] = React.useState<
        ReviewPacketVerticesData[] | null
    >(null);
    const [rightVerticesData, setRightVertices] = React.useState<
        ReviewPacketVerticesData[] | null
    >(null);
    const [isMissingLeftDocument, setMissingLeftDocument] =
        React.useState<boolean>(false);
    const [isMissingRightDocument, setMissingRightDocument] =
        React.useState<boolean>(false);
    const [leftDrawn, setLeftDrawn] = useState<boolean>(false);
    const [rightDrawn, setRightDrawn] = useState<boolean>(false);
    const [leftCompareValue, setLeftCompareValue] = React.useState<string>('');
    const [isContextNeedRestore, setContextNeedRestore] =
        React.useState<boolean>(false);
    const [rightCompareValue, setRightCompareValue] =
        React.useState<string>('');
    const [leftAbortProtocol, setLeftAbortProtocol] =
        React.useState<AbortController | null>(null);
    const [rightAbortProtocol, setRightAbortProtocol] =
        React.useState<AbortController | null>(null);

    // React.useEffect(() => {
    //     dispatch(CarryAppActions.setZoomLeftDocument(10));
    //     dispatch(CarryAppActions.setZoomRightDocument(10));
    //     dispatch(CarryAppActions.setZoomSingleLeftDocument(20));
    //     dispatch(CarryAppActions.setZoomSingleRightDocument(20));
    // }, []);
    const [tooltipMeasurement, setTooltipMeasurement] = useState<LayoutOffset>({
        width: 0,
        height: 0,
    });
    const [firstReasonRework, setFirstReasonRework] = useState<string>('');
    const [secondReasonRework, setSecondReasonRework] = useState<string>('');
    const [footerMeasurement, setFooterMeasurement] = useState<LayoutOffset>({
        width: 0,
        height: 0,
    });
    const { width, height } = useWindowSize();
    const [isCachedRestore, setCachedRestored] = useState<boolean>(false);

    const cachedBackState = useMemo(() => browserProps?.backState, []);
    const documentReloaded = useMemo(() => browserProps?.isDocumentReload, []);

    const isRestorePreviousState = useMemo(
        () =>
            ((router && router.action !== 'REPLACE') || isCachedRestore) &&
            cachedBackState !== null,
        [isCachedRestore, router]
    );

    const isLocationPrimaryRoute = useMemo(
        () =>
            router.previousLocations.some(
                (e: any, index: number) =>
                    e.location.pathname === '/manage-packets' && index === 1
            ),
        [location.key, recordLocations]
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isRestoreLastState = useMemo(
        () =>
            ((router && router.action !== 'REPLACE') || isCachedRestore) &&
            isLocationPrimaryRoute === false &&
            documentReloaded,
        [isCachedRestore, documentReloaded, isLocationPrimaryRoute, router]
    );
    const [viewerLoading, setViewerLoading] = useState<boolean>(true);
    const broadcastReviewedArray = useSelector(
        (state: any) => state.broadcastState.broadcastReviewedData
    );

    const defaultState: defaultReviewedState = useMemo(() => {
        if (isRestorePreviousState || isRestoreLastState) {
            const packetActionsDocumentdataUUID =
                location.state &&
                location.state.documentData &&
                location.state.documentData.actions &&
                location.state.documentData.actions.uuid
                    ? location.state.documentData.actions.uuid
                    : undefined;
            const packetActionsUUID =
                location.state &&
                location.state.actions &&
                location.state.actions.uuid
                    ? location.state.actions.uuid
                    : packetActionsDocumentdataUUID;
            const reviewData =
                location.state && location.state.reviewData
                    ? location.state.reviewData
                    : undefined;
            const prevLocation = broadcastReviewedArray.find(
                (e: any) =>
                    e.pathname === '/single-reviewed' &&
                    e &&
                    e.state !== null &&
                    e.state.documentData &&
                    e.state.documentData.actions &&
                    e.state.documentData.actions.uuid &&
                    e.state.documentData.actions.uuid === packetActionsUUID
            );

            const prevLocationState =
                prevLocation && prevLocation.state
                    ? prevLocation.state
                    : undefined;

            const prevState =
                isRestorePreviousState && cachedBackState
                    ? cachedBackState
                    : prevLocationState;
            const prevDocumentData =
                prevState && prevState.documentData
                    ? prevState.documentData
                    : undefined;
            return {
                ...location.state,
                ...prevDocumentData,
                reviewData:
                    prevState && prevState.reviewData
                        ? prevState.reviewData
                        : reviewData,
            };
        }
        if (
            location.state === null &&
            router.location &&
            typeof router.location.state === 'object' &&
            router.location.pathname === location.pathname
        ) {
            return router.location.state;
        }
        return location.state;
    }, [isRestoreLastState, isRestorePreviousState]);

    useLayoutEffect(() => {
        if (isRestorePreviousState || isRestoreLastState) {
            dispatch({
                type: LOCATION_CHANGE,
                payload: {
                    location: {
                        ...location,
                        state: defaultState,
                    },
                    action: Action.Replace,
                },
            });
            browserProps?.setBackState(null);
            setCachedRestored(true);
        }
    }, [isRestorePreviousState, isRestoreLastState]);
    /** @constant {boolean} - memo to indicate page restored / already reviewed */
    const isRestore = useMemo(() => {
        if (
            defaultState &&
            typeof defaultState.reviewData !== 'undefined' &&
            typeof defaultState.actions === 'object' &&
            defaultState.actions !== null &&
            typeof defaultState.actions.uuid !== 'undefined'
        ) {
            return true;
        }
        return false;
    }, [defaultState, isRestorePreviousState]);

    /** @constant {string} - uuid from actions state */
    const packetId = useMemo(
        () =>
            defaultState && defaultState.actions && defaultState.actions.uuid
                ? defaultState.actions.uuid
                : '',
        [defaultState, isRestorePreviousState]
    );

    const isPageLeave = useSelector(
        (state: any) => state.carryState.isPageLeave
    );

    const currentIdx = useMemo(
        () =>
            window.history.state &&
            typeof window.history.state.idx !== 'undefined'
                ? window.history.state.idx
                : null,
        []
    );
    const broadcastSolicitorArray = useSelector(
        (state: any) => state.broadcastState.broadcastSolicitorData
    );
    const isRecordLocale = useMemo(
        () =>
            broadcastSolicitorArray &&
            broadcastSolicitorArray &&
            broadcastSolicitorArray.filter(
                (e: any) =>
                    e.pathname === '/reviewer-viewer' &&
                    e &&
                    e.state !== null &&
                    e.state.actions &&
                    e.state.actions.uuid &&
                    e.state.actions.uuid === packetId
            ).length > 1 &&
            isPageLeave,
        [broadcastSolicitorArray, isPageLeave, packetId]
    );

    /** @constant {string} - clientId from packets */
    const clientId = useMemo(() => {
        if (defaultState && typeof defaultState.reviewData !== 'undefined') {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { reviewData, ...documentState } = defaultState;
            return documentState.clientId;
        }
        return defaultState && defaultState.clientId
            ? defaultState.clientId
            : '';
    }, [defaultState, isRestorePreviousState]);
    const [arraySteps, setSteps] = useState<Steps[]>([]);
    const [AbortRightProtocol, setAbortRightProtocol] =
        React.useState<boolean>(false);
    const [AbortLeftProtocol, setAbortLeftProtocol] =
        React.useState<boolean>(false);
    const [rightDelayedSastoken, setRightDelayedSastoken] =
        useState<boolean>(false);
    const [leftDelayedSastoken, setLeftDelayedSastoken] =
        useState<boolean>(false);
    const [progressionLeftSasToken, setProgressionLeftSasToken] =
        useState<boolean>(false);
    const [progressionRightSasToken, setProgressionRightSasToken] =
        useState<boolean>(false);
    const [, cancelRight, resetRight] = useTimeoutFn(() => {
        setProgressionRightSasToken(false);
        if (rightDelayedSastoken) {
            setRightDelayedSastoken(false);
        }
    }, 200);
    useEffect(() => {
        cancelRight();
        if (rightDelayedSastoken) {
            setProgressionRightSasToken(true);
        } else {
            resetRight();
        }
    }, [rightDelayedSastoken]);
    const [, cancelLeft, resetLeft] = useTimeoutFn(() => {
        setProgressionLeftSasToken(false);
        if (leftDelayedSastoken) {
            setLeftDelayedSastoken(false);
        }
    }, 200);
    useEffect(() => {
        cancelLeft();
        if (leftDelayedSastoken) {
            setProgressionLeftSasToken(true);
        } else {
            resetLeft();
        }
    }, [leftDelayedSastoken]);

    // useHarmonicIntervalFn(
    //     () => {
    //         setProgressionRightSasToken(false);
    //     },
    //     rightDelayedSastoken === false ? 200 : null
    // );
    // useHarmonicIntervalFn(
    //     () => {
    //         setProgressionLeftSasToken(false);
    //     },
    //     leftDelayedSastoken === false ? 200 : null
    // );
    const handleRevocationRight = useCallback(
        (e: any) => {
            if (e.data === 'revocation') {
                setRightDelayedSastoken(false);

                if (AbortRightProtocol) {
                    setAbortRightProtocol(false);
                }
            }
        },
        [AbortRightProtocol]
    );
    const handleRevocationLeft = useCallback(
        (e: any) => {
            if (e.data === 'revocation') {
                setLeftDelayedSastoken(false);
                if (AbortLeftProtocol) {
                    setAbortLeftProtocol(false);
                }
            }
        },
        [AbortLeftProtocol]
    );
    useDebounce(
        () => {
            if (leftAbortProtocol) {
                const messageChannel: MessageChannel =
                    ManagePacketService.getBoundEventPacketService();

                const portTwo: MessagePort = messageChannel.port2;
                portTwo.addEventListener('message', handleRevocationLeft);
            }
        },
        200,
        [currentStepNumber, leftAbortProtocol]
    );

    useDebounce(
        () => {
            if (rightAbortProtocol) {
                const messageChannel: MessageChannel =
                    ManagePacketService.getBoundEventPacketService();
                const portOne: MessagePort = messageChannel.port1;
                portOne.addEventListener('message', handleRevocationRight);
            }
        },
        200,
        [currentStepNumber, rightAbortProtocol]
    );
    useDebounce(
        () => {
            if (AbortLeftProtocol) {
                const messageChannel: MessageChannel =
                    ManagePacketService.getBoundEventPacketService();
                const portTwo: MessagePort = messageChannel.port2;
                const portOne: MessagePort = messageChannel.port1;
                portTwo.addEventListener('message', handleRevocationLeft);
                portOne.postMessage('revocation');
            }
        },
        200,
        [currentStepNumber, AbortLeftProtocol]
    );

    useDebounce(
        () => {
            if (AbortRightProtocol) {
                const messageChannel: MessageChannel =
                    ManagePacketService.getBoundEventPacketService();
                const portOne: MessagePort = messageChannel.port1;
                const portTwo: MessagePort = messageChannel.port1;
                portOne.addEventListener('message', handleRevocationRight);
                portTwo.postMessage('revocation');
            }
        },
        200,
        [currentStepNumber, AbortRightProtocol]
    );

    const handleGetSasToken = React.useCallback(
        (url: string | undefined | null, placement: 'left' | 'right') => {
            if (typeof url === 'string') {
                return new Promise<any>(async (resolve, reject) => {
                    const messageChannel: MessageChannel =
                        ManagePacketService.getBoundEventPacketService();
                    const portOne: MessagePort = messageChannel.port1;
                    const portTwo: MessagePort = messageChannel.port2;
                    if (placement === 'right') {
                        portOne.start();
                    } else if (placement === 'left') {
                        portTwo.start();
                    }
                    ManagePacketService.getSasToken({
                        fileName: url,
                        report: false,
                    })
                        .then(resolve)
                        .catch(reject);

                    if (placement === 'left') {
                        setLeftAbortProtocol(
                            ManagePacketService.getAbortProtocol()
                        );
                    } else if (placement === 'right') {
                        setRightAbortProtocol(
                            ManagePacketService.getAbortProtocol()
                        );
                    }
                });
            }
            return new Promise((reject) => {
                reject(null);
                if (placement === 'left') {
                    setAbortLeftProtocol(true);
                } else if (placement === 'right') {
                    setAbortRightProtocol(true);
                }
            });
        },
        []
    );
    const [isNetworkErrorRight, setNetworkErrorRight] =
        useState<boolean>(false);
    const [isNetworkErrorLeft, setNetworkErrorLeft] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [
        retrySasTokenRight,
        resetRetryingSasRight,
        stopRetryingSasRight,
        getRetryingSasRight,
    ] = usePromiseRetry(
        () => {
            if (rightAbortProtocol) {
                rightAbortProtocol.abort();
            }
        },
        () => {
            // console.log('sas', 2);
        },
        {
            retries: 5,
        }
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [
        retrySasTokenLeft,
        resetRetryingSasLeft,
        stopRetryingSasLeft,
        getRetryingSasLeft,
    ] = usePromiseRetry(
        () => {
            if (leftAbortProtocol) {
                leftAbortProtocol.abort();
            }
        },
        () => {
            // console.log('sas', 4);
        },
        {
            retries: 5,
        }
    );

    const promiseRetryGetSasToken = React.useCallback(
        (
            sequence: number,
            url: string | undefined | null,
            placement: 'left' | 'right'
        ) => {
            if (placement === 'right') {
                return retrySasTokenRight(
                    (retry) =>
                        handleGetSasToken(url, placement).catch((e) => {
                            if (
                                e !== null &&
                                e instanceof AxiosError &&
                                e.code === 'ERR_NETWORK'
                            ) {
                                setNetworkErrorRight(true);
                            } else if (
                                e.response &&
                                e.response.status !== 401
                            ) {
                                retry(e);
                            } else if (
                                typeof e.response === 'undefined' &&
                                e instanceof CanceledError === false
                            ) {
                                retry(e);
                            }
                        }),
                    sequence
                );
            }
            return retrySasTokenLeft(
                (retry) =>
                    handleGetSasToken(url, placement).catch((e) => {
                        if (
                            e !== null &&
                            e instanceof AxiosError &&
                            e.code === 'ERR_NETWORK'
                        ) {
                            setNetworkErrorLeft(true);
                        } else if (e.response && e.response.status !== 401) {
                            retry(e);
                        } else if (
                            typeof e.response === 'undefined' &&
                            e instanceof CanceledError === false
                        ) {
                            retry(e);
                        }
                    }),
                sequence
            );
        },
        [handleGetSasToken, retrySasTokenLeft, retrySasTokenRight]
    );
    React.useLayoutEffect(() => {
        const { current: documentViewer } = documentViewerRef;
        if (isContextNeedRestore) {
            if (documentViewer) {
                documentViewer.setRestoreContext();
            }
            setContextNeedRestore(false);
        }
    }, [isContextNeedRestore]);
    const preSelectMissingDocument = React.useCallback(
        (currentStep: ReviewPacketData) => {
            if (currentStep?.result === 'Document Missing') {
                return true;
            }
            if (currentStep && currentStep.result !== 'Paper Document') {
                if (
                    currentStep.foundDocuments === 2 &&
                    currentStep.requiredDocuments === 2 &&
                    ((typeof currentStep.compareTo === 'object' &&
                        (currentStep.compareTo.documentId === null ||
                            typeof currentStep.compareTo.documentId ===
                                'undefined')) ||
                        (typeof currentStep.compareWith === 'object' &&
                            (currentStep.compareWith.documentId === null ||
                                typeof currentStep.compareWith.documentId ===
                                    'undefined')))
                ) {
                    return true;
                }
                if (
                    currentStep.foundDocuments === 2 &&
                    currentStep.requiredDocuments === 1 &&
                    typeof currentStep.compareTo === 'object' &&
                    (currentStep.compareTo.documentId === null ||
                        typeof currentStep.compareTo.documentId ===
                            'undefined') &&
                    typeof currentStep.compareWith === 'object' &&
                    (currentStep.compareWith.documentId === null ||
                        typeof currentStep.compareWith.documentId ===
                            'undefined')
                ) {
                    return true;
                }
                if (
                    currentStep.foundDocuments === 1 &&
                    currentStep.requiredDocuments === 1 &&
                    typeof currentStep.compareTo === 'object' &&
                    (currentStep.compareTo.documentId === null ||
                        typeof currentStep.compareTo.documentId ===
                            'undefined') &&
                    typeof currentStep.compareWith === 'object' &&
                    (currentStep.compareWith.documentId === null ||
                        typeof currentStep.compareWith.documentId ===
                            'undefined')
                ) {
                    return true;
                }
                if (
                    currentStep.foundDocuments === 1 &&
                    currentStep.requiredDocuments === 2 &&
                    ((typeof currentStep.compareTo === 'object' &&
                        (currentStep.compareTo.documentId === null ||
                            typeof currentStep.compareTo.documentId ===
                                'undefined')) ||
                        (typeof currentStep.compareWith === 'object' &&
                            (currentStep.compareWith.documentId === null ||
                                typeof currentStep.compareWith.documentId ===
                                    'undefined')))
                ) {
                    return true;
                }
                if (
                    currentStep.foundDocuments === 0 &&
                    currentStep.requiredDocuments === 1 &&
                    typeof currentStep.compareTo === 'object' &&
                    (currentStep.compareTo.documentId === null ||
                        typeof currentStep.compareTo.documentId ===
                            'undefined') &&
                    typeof currentStep.compareWith === 'object' &&
                    (currentStep.compareWith.documentId === null ||
                        typeof currentStep.compareWith.documentId ===
                            'undefined')
                ) {
                    return true;
                }
            }
            return false;
        },
        []
    );
    const [isCurrentStepRefresh, setCurrentStepRefresh] =
        useState<boolean>(false);
    const [isCurrentStepPdfNetworkError, setCurrentStepPDFNetworkError] =
        useState<boolean>(false);

    const currentStepDocumentRefresh = useCallback(
        (currentStepData: ReviewData) => {
            const { current: viewer } = documentViewerRef;
            if (currentStepNumber) {
                resetRetryingSasLeft(currentStepNumber);
                resetRetryingSasRight(currentStepNumber);
            }
            setNetworkErrorLeft(false);
            setNetworkErrorRight(false);
            if (leftAbortProtocol) {
                leftAbortProtocol.abort();
            }
            if (rightAbortProtocol) {
                rightAbortProtocol.abort();
            }
            if (leftAbortProtocol || rightAbortProtocol) {
                const messageChannel: MessageChannel =
                    ManagePacketService.getBoundEventPacketService();
                const portOne: MessagePort = messageChannel.port1;
                const portTwo: MessagePort = messageChannel.port2;
                portOne.close();
                portTwo.close();

                ManagePacketService.resetEventPacketService();
                setLeftDelayedSastoken(true);
                setRightDelayedSastoken(true);
            }
            const isMissingDocument = preSelectMissingDocument(currentStepData);

            const { compareTo, compareWith, type, result } = currentStepData;
            const isLeftAsPacket =
                typeof compareWith === 'object' &&
                Object.keys(compareWith).filter(
                    (o) => o !== 'sortedVertices' && o !== 'coverLetterDetails'
                ).length > 0;
            const isRightAsPacket =
                typeof compareTo === 'object' &&
                Object.keys(compareTo).filter(
                    (o) => o !== 'sortedVertices' && o !== 'coverLetterDetails'
                ).length > 0;
            setLeftAsPacket(isLeftAsPacket);
            setRightAsPacket(isRightAsPacket);
            if (isMissingDocument && compareWith.file === null) {
                setLeftCategory(compareWith.category);
                setMissingLeftDocument(true);
            } else {
                setMissingLeftDocument(false);
                setLeftCategory(compareWith.category);
            }
            if (isMissingDocument && compareTo.file === null) {
                setMissingRightDocument(true);
                setRightCategory(compareTo.category);
            } else {
                setMissingRightDocument(false);
                setRightCategory(compareTo.category);
            }

            setLeftFilename(compareWith.placeholder);
            setRightFilename(compareTo.placeholder);
            setLeftVertices(
                typeof compareWith.coverLetterDetails === 'object' &&
                    compareWith.coverLetterDetails !== null &&
                    typeof compareWith.coverLetterDetails.sortedVertices ===
                        'object' &&
                    compareWith.coverLetterDetails.sortedVertices !== null
                    ? compareWith.coverLetterDetails.sortedVertices
                    : compareWith.sortedVertices
            );
            setRightVertices(
                typeof compareTo.coverLetterDetails === 'object' &&
                    compareTo.coverLetterDetails !== null &&
                    typeof compareTo.coverLetterDetails.sortedVertices ===
                        'object' &&
                    compareTo.coverLetterDetails.sortedVertices !== null
                    ? compareTo.coverLetterDetails.sortedVertices
                    : compareTo.sortedVertices
            );
            setLeftCompareValue(compareWith.value);
            setRightCompareValue(compareTo.value);
            setLeftDrawn(compareWith.userDrawn ?? false);
            setCurrentType(type);
            setRightDrawn(compareTo.userDrawn ?? false);

            Promise.allSettled([
                promiseRetryGetSasToken(
                    currentStepData.sequence,
                    (isMissingDocument && compareWith.file === null) ||
                        (result === 'Correct' &&
                            compareWith.coverLetterDetails !== null)
                        ? compareWith.coverLetterDetails?.file
                        : compareWith.file,
                    'left'
                ),
                promiseRetryGetSasToken(
                    currentStepData.sequence,
                    (isMissingDocument && compareTo.file === null) ||
                        (result === 'Correct' &&
                            compareTo.coverLetterDetails !== null)
                        ? compareTo.coverLetterDetails?.file
                        : compareTo.file,
                    'right'
                ),
            ])
                .then((responseArray) => {
                    // console.log('response array', responseArray);
                    setContextNeedRestore(true);
                    const previousLeftDocument = leftDocument;
                    const previousRightDocument = rightDocument;
                    // eslint-disable-next-line no-underscore-dangle
                    let _leftDocument = null;
                    // eslint-disable-next-line no-underscore-dangle
                    let _rightDocument = null;

                    if (
                        responseArray[0].status === 'fulfilled' &&
                        typeof responseArray[0].value === 'object'
                    ) {
                        if (responseArray[0].value !== null) {
                            const leftResponseUrlSaS =
                                responseArray[0].value.data.data.url;
                            setLeftDocument(leftResponseUrlSaS);
                            _leftDocument = leftResponseUrlSaS;
                        } else {
                            setLeftDocument(null);
                        }
                    } else if (
                        responseArray[0].status === 'rejected' &&
                        responseArray[0].reason instanceof CanceledError ===
                            false
                    ) {
                        setLeftDocument(null);
                    }
                    if (
                        responseArray[1].status === 'fulfilled' &&
                        typeof responseArray[1].value === 'object'
                    ) {
                        if (responseArray[1].value !== null) {
                            const rightResponseUrlSaS =
                                responseArray[1].value.data.data.url;
                            setRightDocument(rightResponseUrlSaS);
                            _rightDocument = rightResponseUrlSaS;
                        } else {
                            setRightDocument(null);
                        }
                    } else if (
                        responseArray[1].status === 'rejected' &&
                        responseArray[1].reason instanceof CanceledError ===
                            false
                    ) {
                        setRightDocument(null);
                    }
                    if (viewer) {
                        if (
                            previousLeftDocument === _leftDocument ||
                            (_leftDocument === null && isLeftAsPacket)
                        ) {
                            viewer?.setNonNullableLeftViewer();
                            viewer?.setLeftDocumentNullable(_leftDocument);
                        }
                        if (
                            previousRightDocument === _rightDocument ||
                            (_rightDocument === null && isRightAsPacket)
                        ) {
                            viewer?.setNonNullableRightViewer();
                            viewer?.setRightDocumentNullable(_rightDocument);
                        }
                    }
                })
                .catch((error) => {
                    setContextNeedRestore(true);
                    Utils.handleErrorResponse(error);
                })
                .finally(() => {
                    const messageChannel: MessageChannel =
                        ManagePacketService.getBoundEventPacketService();
                    const portOne: MessagePort = messageChannel.port1;
                    const portTwo: MessagePort = messageChannel.port2;
                    portOne.close();
                    portTwo.close();
                    resetLeft();
                    resetRight();
                });
        },
        [
            currentStepNumber,
            leftAbortProtocol,
            leftDocument,
            preSelectMissingDocument,
            promiseRetryGetSasToken,
            resetLeft,
            resetRetryingSasLeft,
            resetRetryingSasRight,
            resetRight,
            rightAbortProtocol,
            rightDocument,
        ]
    );
    useUpdateEffect(() => {
        if (currentStepData) {
            currentStepDocumentRefresh(currentStepData);
        }
    }, [currentStepNumber]);

    useUpdateEffect(() => {
        if (currentStepData && isCurrentStepRefresh) {
            setCurrentStepRefresh(false);
            currentStepDocumentRefresh(currentStepData);
        }
    }, [isCurrentStepRefresh]);

    /**
     * preselect result value used in choose categories to validate the reviewed document
     * @param {ReviewPacketData[]} data - list current reviewed packets
     * @return {ReviewPacketData[]} changes in result for reviewed packets.
     */
    const getPreselectValidationResultForVerificationLabel = React.useCallback(
        (data: ReviewPacketData[]): ReviewPacketData[] =>
            Array.from(data).map((el) => {
                const isMissingDocument = preSelectMissingDocument(el);
                const result = isMissingDocument ? 'Document Missing' : 'N/A';
                return {
                    ...el,
                    result:
                        el.result === 'Correct' ||
                        el.result === 'Document Missing' ||
                        el.result === 'Escalated' ||
                        el.result === 'N/A' ||
                        el.result === 'Send For Rework' ||
                        el.result === 'Paper Document' ||
                        el.result === 'Hand Signed' ||
                        el.result === 'Sign Not Verified'
                            ? el.result
                            : result,
                };
            }),
        [preSelectMissingDocument]
    );

    const setActionObjsToReviewResponse = React.useCallback(
        (data: ReviewPacketData[]) =>
            Array.from(data).map((el) => ({
                ...el,
                isRead: false,
                actions: formattingAction(el),
            })),
        []
    );
    const transitionToDetailReviewed = React.useCallback(
        (data: ReviewPacketData[]) => {
            navigate('/single-reviewed', {
                replace: true,
                state: {
                    reviewData: setActionObjsToReviewResponse(data),
                    documentData: defaultPacketState,
                    primary: true,
                },
            });
        },
        [defaultPacketState, navigate, setActionObjsToReviewResponse]
    );
    React.useLayoutEffect(() => {
        if (
            typeof packetId !== 'undefined' &&
            packetId !== null &&
            typeof isRestore === 'boolean' &&
            !isRestore
        ) {
            ManagePacketService.getReview({ packetId })
                .then((response: { data: { data: ReviewPacketData[] } }) => {
                    const { current: footerRef } = innerFooterRef;
                    const dataKnownInstance = response.data.data.map((val) => {
                        const stack = new Vertices();
                        stack.current_object = val;

                        return {
                            ...val,
                            stack,
                        };
                    });

                    // transition to detail reviewed
                    if (
                        dataKnownInstance.filter((b) =>
                            // eslint-disable-next-line no-underscore-dangle
                            b.stack._knownInstance(b.stack)
                        ).length === 0
                    ) {
                        transitionToDetailReviewed(response.data.data);
                    }
                    const dataHaveKnownInstance = dataKnownInstance.map(
                        (val) => {
                            const updateStack = val.stack;
                            const verticesSystem: VerticeSystem =
                                val.stack as VerticeSystem;

                            updateStack.matching = verticesSystem;

                            return {
                                ...val,
                                stack: verticesSystem,
                            };
                        }
                    );

                    const preSelectData =
                        getPreselectValidationResultForVerificationLabel(
                            dataHaveKnownInstance
                        );

                    const preSelectDataWithAction =
                        setActionObjsToReviewResponse(preSelectData);
                    const reduceToFewerStep = preSelectDataWithAction.reduce(
                        (o, i) => {
                            if (o > i.sequence && i.stack.knownInstance()) {
                                return i.sequence;
                            }
                            return o;
                        },
                        99
                    );

                    const currentStepDataResponse =
                        preSelectDataWithAction.find(
                            (o) => o.sequence === reduceToFewerStep
                        );

                    if (currentStepDataResponse) {
                        if (footerRef !== null) {
                            footerRef.setPacketdata(clientId, packetId);
                            footerRef.setReviewedData(
                                preSelectDataWithAction,
                                reduceToFewerStep,
                                currentStepDataResponse
                            );
                        }
                        setOriginalData(preSelectDataWithAction);
                        setStepData(currentStepDataResponse);
                        setStepNumber(currentStepDataResponse.sequence);
                    }

                    setSteps(
                        Array.from(preSelectDataWithAction).map((el) => ({
                            solicitor: '',
                            datepickerRework: dateFns
                                .addDays(new Date(), el.timeToRework ?? 0)
                                .toString(),
                            firstReason: '',
                            secondReason: '',
                            escalatedReason:
                                typeof el.message === 'string'
                                    ? el.message
                                    : '',
                            isSecondDocument: false,
                            isFirstDocument: false,
                            isEscalated: el.result === 'Escalated',
                            isRework: el.result === 'Send For Rework',
                            isDisplayedEscalatedModal:
                                el.result === 'Escalated',
                            firstReasonPreDefined: '',
                            secondReasonPreDefined: '',
                        }))
                    );
                    setViewerLoading(false);
                })
                .catch((error) => {
                    Utils.handleErrorResponse(error);
                    if (currentIdx === 0) {
                        navigate('/manage-packets', { replace: true });
                    } else {
                        navigate(-1);
                    }
                });
        } else if (
            typeof defaultState === 'object' &&
            defaultState.reviewData
        ) {
            const reduceToFewerStepDataRestore = defaultState.reviewData
                .map((val) => {
                    const stack = new Vertices();
                    stack.current_object = val;
                    // eslint-disable-next-line no-underscore-dangle
                    stack._knownInstance(stack);
                    stack.matching = val.stack;
                    return {
                        ...val,
                        stack,
                    };
                })
                .reduce((o, i) => {
                    if (o > i.sequence && i.stack.knownInstance()) {
                        return i.sequence;
                    }
                    return o;
                }, 99);
            const currentStepDataRestore = defaultState.reviewData.find(
                (o) => o.sequence === reduceToFewerStepDataRestore
            );
            if (currentStepDataRestore) {
                const { current: footerRef } = innerFooterRef;
                if (footerRef !== null) {
                    footerRef.setPacketdata(clientId, packetId);
                    footerRef.setReviewedData(
                        defaultState.reviewData,

                        reduceToFewerStepDataRestore,
                        currentStepDataRestore
                    );
                }
                setOriginalData(defaultState.reviewData);
                setStepData(currentStepDataRestore);
                setStepNumber(currentStepDataRestore.sequence);
            }

            setSteps(
                Array.from(defaultState.reviewData ?? [])
                    .sort((a, b) => b.sequence - a.sequence)
                    .map((el) => {
                        const escalationMessage =
                            el.isRead === false
                                ? el.message
                                : el.escalatedReason;
                        return {
                            solicitor: el.solicitorData
                                ? `${el.solicitorData.name} - ${el.solicitorData.email.id}`
                                : '',
                            datepickerRework: el.expiryDate ?? '',
                            firstReason: el.firstReason ?? '',
                            secondReason: el.secondReason ?? '',
                            firstReasonPreDefined:
                                el.firstReasonPreDefined ?? '',
                            secondReasonPreDefined:
                                el.secondReasonPreDefined ?? '',
                            escalatedReason: escalationMessage ?? '',
                            isSecondDocument: !!el.secondReason,
                            isFirstDocument: !!el.firstReason,
                            isEscalated: el.result === 'Escalated',
                            isRework: el.result === 'Send For Rework',
                            isDisplayedEscalatedModal:
                                el.result === 'Escalated',
                        };
                    })
            );
            setViewerLoading(false);
        }
    }, []);

    /**
     * action when left document can be drawn and trigger callback
     * @param {number[][]} corners - coordinate clockwise from top left
     * @param {number} pageNumber - current document page number
     * @param {number} ratio - ratio comparison between original and current scale
     */
    const actionOnDrawLeftDocument = React.useCallback(
        (corners: number[][], pageNumber: number, ratio: number) => {
            const { current: footerRef } = innerFooterRef;
            footerRef?.updateCompareWithVerticesData(
                corners,
                pageNumber,
                ratio
            );
        },
        []
    );

    /**
     * action when right document can be drawn and trigger callback
     * @param {number[][]} corners - coordinate clockwise from top left
     * @param {number} pageNumber - current document page number
     * @param {number} ratio - ratio comparison between original and current scale
     */
    const actionOnDrawRightDocument = React.useCallback(
        (corners: number[][], pageNumber: number, ratio: number) => {
            const { current: footerRef } = innerFooterRef;
            footerRef?.updateCompareToVerticesData(corners, pageNumber, ratio);
        },
        []
    );
    /**
     * action when left document saved vertices is deleted, only saved vertices
     * @param {number} index - vertices active indice
     * @param {boolean} isSaved - conditional check between saved or non-saved vertices
     */
    const actionOnEmptyDrawLeftDocument = React.useCallback(
        (index: number, isSaved: boolean) => {
            const { current: footerRef } = innerFooterRef;
            const data = footerRef?.emptyCompareWithVerticesData(
                index,
                isSaved
            );

            if (data && isSaved) {
                const currentStepDataDrawOnLeft = data.find(
                    (o) => o.actions.uuid === currentStepData?.actions.uuid
                );

                if (currentStepDataDrawOnLeft) {
                    setLeftVertices(
                        (
                            currentStepDataDrawOnLeft.compareWith
                                .sortedVertices ?? []
                        ).filter(
                            (o) =>
                                o.isActive === false ||
                                typeof o.isActive === 'undefined'
                        )
                    );
                }
            }
        },

        [currentStepData?.actions.uuid]
    );
    /**
     * action when right document saved vertices is deleted, only saved vertices
     * @param {number} index - vertices active indice
     * @param {boolean} isSaved - conditional check between saved or non-saved vertices
     */
    const actionOnEmptyDrawRightDocument = React.useCallback(
        (index: number, isSaved: boolean) => {
            const { current: footerRef } = innerFooterRef;
            const data = footerRef?.emptyCompareToVerticesData(index, isSaved);

            if (data && isSaved) {
                const currentStepDataDrawnOnRight = data.find(
                    (o) => o.actions.uuid === currentStepData?.actions.uuid
                );

                if (currentStepDataDrawnOnRight) {
                    setRightVertices(
                        (
                            currentStepDataDrawnOnRight.compareTo
                                .sortedVertices ?? []
                        ).filter(
                            (o) =>
                                o.isActive === false ||
                                typeof o.isActive === 'undefined'
                        )
                    );
                }
            }
        },

        [currentStepData?.actions.uuid]
    );
    /**
     * callback from footer when step is changed
     * @param {number} currentStep - numerical step value
     * @param {ReviewPacketData} _currentStepData - changed step data / active step data after the step is change.
     */
    const handleChangedStepOnFooter = React.useCallback(
        async (currentStep: number, _currentStepData: ReviewPacketData) => {
            const { current: documentViewer } = documentViewerRef;

            if (documentViewer) {
                documentViewer.setLoseContext().then(() => {
                    // setContextNeedRestore(true);
                    setStepData(_currentStepData);
                    setStepNumber(currentStep);
                });
            } else {
                setStepData(_currentStepData);
                setStepNumber(currentStep);
            }
        },
        []
    );
    const handleClickStepOnFooter = React.useCallback(() => {
        const { current: documentViewer } = documentViewerRef;
        if (documentViewer) {
            documentViewer.setPreloadDocument();
        }
        if (currentStepNumber) {
            stopRetryingSasLeft(currentStepNumber);
            stopRetryingSasRight(currentStepNumber);
            if (
                isNetworkErrorLeft ||
                isNetworkErrorRight ||
                isCurrentStepPdfNetworkError
            ) {
                setCurrentStepRefresh(true);
            } else if (currentStepNumber) {
                // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
                const attempsLeft = getRetryingSasLeft(currentStepNumber);
                const attempsRight = getRetryingSasRight(currentStepNumber);

                if (attempsLeft === 6 && leftDocument === null) {
                    setCurrentStepRefresh(true);
                } else if (attempsRight === 6 && rightDocument === null) {
                    setCurrentStepRefresh(true);
                }
            }

            setNetworkErrorLeft(false);
            setNetworkErrorRight(false);
        }
    }, [
        currentStepNumber,
        getRetryingSasLeft,
        getRetryingSasRight,
        isCurrentStepPdfNetworkError,
        isNetworkErrorLeft,
        isNetworkErrorRight,
        leftDocument,
        rightDocument,
        stopRetryingSasLeft,
        stopRetryingSasRight,
    ]);
    const [footerCategory, setFooterCategory] = useState<string>();

    // useLayoutEffect(() => {
    //     if (footerCategory === 'Document Missing') {
    //         const { current: footerRef } = innerFooterRef;
    //         const data = footerRef?.removeVerticesDataWithMissingDocument(
    //             isMissingLeftDocument,
    //             isMissingRightDocument
    //         );
    //         if (data) {
    //             const currentStepDataDraw = data.find(
    //                 (o) => o.sequence === currentStepData?.sequence
    //             );

    //             if (isLeftPacket && currentStepDataDraw) {
    //                 setLeftVertices(
    //                     currentStepDataDraw.compareWith.sortedVertices ?? []
    //                 );
    //             }

    //             if (isRightPacket && currentStepDataDraw) {
    //                 setRightVertices(
    //                     currentStepDataDraw.compareTo.sortedVertices ?? []
    //                 );
    //             }
    //         }
    //     }
    // }, [footerCategory]);

    /**
     * callback from footer when reviewed document categories is changed
     * @param {string} val - string of selected category
     */
    const handleChangedCategoryOnFooter = React.useCallback((val: string) => {
        setFooterCategory(val);
    }, []);

    /**
     * callback hide footer
     */
    const handleHiddenFooter = React.useCallback(() => {
        innerFooterWrapperRef.current?.setHidden();
    }, []);
    /**
     * callback show footer
     */
    const handleShowFooter = React.useCallback(() => {
        innerFooterWrapperRef.current?.setShow();
    }, []);

    const callbackForPromptAlert = React.useCallback(() => {
        const { current: containerRef } = innerMainRef;
        ManagePacketService.patchOperatorAction({
            open: false,
            packetId,
        })
            .then(() => {
                containerRef?.classList.add('patch');
                navigate('/reviewer-viewer', {
                    replace: true,
                    state: {
                        ...defaultState,
                        isOpen: false,
                    },
                });
                // window.history.state.isOpen = false;
                //               window.history.replaceState({ ba: false }, '');
            })
            .catch(Utils.handleErrorResponse);
    }, [defaultState, navigate, packetId]);

    const [, showLeavePrompt] = useLeavePrompt(false, callbackForPromptAlert);
    const [isLeavePage, setLeavePage] = React.useState<boolean>(false);
    usePageLeave(() => {
        setLeavePage(true);
    });
    const handlePatchingOperatorLockWithTimeout = React.useCallback(() => {
        ManagePacketService.patchOperatorAction({
            open: true,
            packetId: packetId ?? '',
        })
            .then(() => {
                dispatch({
                    type: LOCATION_CHANGE,
                    payload: {
                        location: {
                            ...location,
                            state: {
                                ...defaultState,
                                isOpen: true,
                            },
                        },
                    },
                });
                // const { current: modalRef } = innerModalRef;
                // modalRef?.classList.add('patch');
                // Utils.handleSuccessResponse(response);
                // navigate('/not-expected-packet', {
                //     replace: true,
                //     state: {
                //         ...defaultState,
                //         isOpen: false,
                //     },
                // });
                // window.history.state.isOpen = false;
                //               window.history.replaceState({ ba: false }, '');
            })
            .catch(Utils.handleErrorResponse);
    }, [defaultState, dispatch, location, packetId]);
    const [, cancel, reset] = useTimeoutFn(
        handlePatchingOperatorLockWithTimeout,
        500
    );
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleMouseEnter = React.useCallback((event: any) => {
        const { current: containerRef } = innerMainRef;

        if (isPointerInside(containerRef, event)) {
            if (containerRef?.classList.contains('patch')) {
                containerRef.classList.remove('patch');
                reset();
            }
            setLeavePage(false);
        }
    }, []);
    useUpdateEffect(() => {
        const { current: containerRef } = innerMainRef;
        if (containerRef) {
            if (isLeavePage) {
                containerRef.addEventListener('mouseenter', handleMouseEnter);
            } else {
                containerRef.removeEventListener(
                    'mouseenter',
                    handleMouseEnter
                );
            }
        }
    }, [isLeavePage]);
    // NOTE: this similar to componentWillUnmount()
    React.useEffect(() => {
        if (
            defaultState &&
            typeof defaultState.isOpen !== 'undefined' &&
            defaultState.isOpen === false
        ) {
            ManagePacketService.patchOperatorAction({
                open: true,
                packetId,
            })
                .then(() => {
                    // Utils.handleSuccessResponse(response);
                    navigate('/reviewer-viewer', {
                        replace: true,
                        state: {
                            ...location.state,
                            isOpen: true,
                        },
                    });
                })
                .catch(Utils.handleErrorResponse);
        }
        cancel();
        showLeavePrompt(true);
        return () => {
            cancel();
            showLeavePrompt(false);
        };
    }, []);

    useEffect(() => {
        if (isRecordLocale) {
            ManagePacketService.patchOperatorAction({
                open: true,
                packetId,
            });
        }
    }, []);

    React.useEffect(() => {
        window.addEventListener('error', (e) => {
            if (
                e.message ===
                'ResizeObserver loop completed with undelivered notifications.'
            ) {
                const resizeObserverErrDiv = document.getElementById(
                    'webpack-dev-server-client-overlay-div'
                );
                const resizeObserverErr = document.getElementById(
                    'webpack-dev-server-client-overlay'
                );
                if (resizeObserverErr) {
                    resizeObserverErr.setAttribute('style', 'display: none');
                }
                if (resizeObserverErrDiv) {
                    resizeObserverErrDiv.setAttribute('style', 'display: none');
                }
            }
        });
    }, []);

    React.useLayoutEffect(() => {
        const { current: footerWrapper } = innerFooterWrapperRef;
        if (footerWrapper) {
            const measureFooter = footerWrapper.getMeasureFooter();
            setFooterMeasurement({
                width: measureFooter.width,
                height: measureFooter.height,
            });
        }
    }, [width, height]);
    const initialDocumentMeta = React.useMemo(
        () => ({
            setFirstReasonRework,
            setSecondReasonRework,
            firstReasonRework,
            secondReasonRework,
            setFooterMeasurement,
            setTooltipMeasurement,
            tooltipMeasurement,
            isMissingLeftDocument,
            isMissingRightDocument,
            footerMeasurement,
        }),
        [
            firstReasonRework,
            footerMeasurement,
            isMissingLeftDocument,
            isMissingRightDocument,
            secondReasonRework,
            tooltipMeasurement,
        ]
    );

    const socketEvent = React.useMemo(
        () => socketState.socketData.eventName,
        [socketState.socketData.eventName]
    );

    const isTabActive = useSelector(
        (state: any) => state.carryState.visibilityState
    );
    const broadcastPageLeave = useSelector(
        (state: any) => state.broadcastState.broadcastPageLeave
    );
    const broadcastActiveTabId = useSelector(
        (state: any) => state.broadcastState.broadcastActiveTabId
    );
    const [countingTabActive, setCountTabActive] = useState<number>(0);
    const tabId = useSelector((state: any) => state.carryState.tabId);
    useEffect(() => {
        if (
            document.visibilityState !== 'visible' &&
            broadcastPageLeave === false &&
            isTabActive === true
        ) {
            dispatch(CarryAppActions.SetCarryVisibilityState(!isTabActive));
        }
    }, [broadcastPageLeave, isTabActive]);
    useEffect(() => {
        if (
            document.visibilityState !== 'visible' &&
            broadcastPageLeave === false &&
            isTabActive === true &&
            tabId === broadcastActiveTabId
        ) {
            setCountTabActive(1);
        }
    }, []);

    useUpdateEffect(() => {
        if (tabId !== broadcastActiveTabId) {
            setCountTabActive(0);
        }
    }, [broadcastActiveTabId]);

    const handleVisibilityChange = useCallback(() => {
        dispatch(
            CarryAppActions.SetCarryVisibilityState(
                document.visibilityState === 'visible'
            )
        );
    }, []);

    useEvent('visibilitychange', handleVisibilityChange);
    const [isGoingBackBySocket, setGoingBackBySocket] =
        React.useState<boolean>(false);
    const [isGoingBackBySocketSamePacket, setGoingBackBySocketSamePacket] =
        React.useState<boolean>(false);

    useUpdateEffect(() => {
        if (socketEvent !== null) {
            if (socketEvent === 'operatorAction') {
                if (
                    // eslint-disable-next-line no-underscore-dangle
                    profileState.userInfo._id ===
                        socketState.socketData.operatorId &&
                    isTabActive === false &&
                    location.pathname === '/reviewer-viewer'
                ) {
                    if (countingTabActive === 0) {
                        dispatch(
                            BroadcastActions.SetBroadcastProgression(true)
                        );
                        setGoingBackBySocketSamePacket(
                            socketState.socketData.packetId === packetId
                        );
                        setGoingBackBySocket(
                            socketState.socketData.packetId !== packetId
                        );
                    } else {
                        setCountTabActive((prev) => {
                            if (prev > 0) {
                                return prev - 1;
                            }
                            return prev;
                        });
                    }
                }
            }
            dispatch(SocketActions.ResetSocketEvent({}));
        }
    }, [socketEvent]);
    const broadcastProgression = useSelector(
        (state: any) => state.broadcastState.broadcastProgression
    );

    useLayoutEffect(() => {
        if (broadcastProgression && isTabActive === true) {
            // Toast.showInfoMessage(
            //     'Another packet is already in open state, closing that packet to opening packet'
            // );
            dispatch(BroadcastActions.SetBroadcastProgression(false));
        }
    }, [broadcastProgression]);
    useUpdateEffect(() => {
        if (isGoingBackBySocketSamePacket) {
            dispatch({
                type: LOCATION_CHANGE,
                payload: {
                    location: {
                        ...location,
                        state: {
                            isOpen: false,
                        },
                    },
                    action: Action.Replace,
                },
            });

            if (currentIdx === 0) {
                navigate('/manage-packets', { replace: true });
            } else {
                navigate(-1);
            }
        }
    }, [isGoingBackBySocketSamePacket]);
    useUpdateEffect(() => {
        if (isGoingBackBySocket) {
            if (currentIdx === 0) {
                navigate('/manage-packets', { replace: true });
            } else {
                navigate(-1);
            }
        }
    }, [isGoingBackBySocket]);
    const currentIndexFormikInput = useMemo(
        () =>
            currentOriginalData?.findIndex(
                (e) => e.sequence === currentStepNumber
            ),
        [currentOriginalData, currentStepNumber]
    );
    useUpdateEffect(() => {
        if (isRightPacket) {
            const { current: formik } = formikRef;
            const { current: footer } = innerFooterRef;
            if (formik && footer) {
                if (secondReasonRework) {
                    currentOriginalData?.forEach((e, index) => {
                        if (
                            index !== currentIndexFormikInput &&
                            ((typeof e.compareTo.documentId === 'undefined' &&
                                e.compareTo.category === rightCategory) ||
                                (typeof currentStepData?.compareTo
                                    .documentId !== 'undefined' &&
                                    e.compareTo.documentId ===
                                        currentStepData?.compareTo.documentId &&
                                    ((currentStepData?.compareTo.documentId ===
                                        null &&
                                        e.compareTo.category ===
                                            rightCategory) ||
                                        currentStepData?.compareTo
                                            .documentId !== null)))
                        ) {
                            const helpers = formik.getFieldHelpers(
                                `reviewedSteps[${index}].secondReason`
                            );
                            const fieldHelper = formik.getFieldHelpers(
                                `reviewedSteps[${index}].isSecondDocument`
                            );

                            fieldHelper.setValue(true, false);
                            helpers.setValue(secondReasonRework, false);
                        }
                        if (
                            index !== currentIndexFormikInput &&
                            ((typeof e.compareWith.documentId === 'undefined' &&
                                e.compareWith.category === rightCategory) ||
                                (typeof currentStepData?.compareTo
                                    .documentId !== 'undefined' &&
                                    e.compareWith.documentId ===
                                        currentStepData?.compareTo.documentId &&
                                    ((currentStepData?.compareTo.documentId ===
                                        null &&
                                        e.compareWith.category ===
                                            rightCategory) ||
                                        currentStepData?.compareTo.documentId)))
                        ) {
                            const helpers = formik.getFieldHelpers(
                                `reviewedSteps[${index}].firstReason`
                            );
                            const fieldHelper = formik.getFieldHelpers(
                                `reviewedSteps[${index}].isFirstDocument`
                            );

                            fieldHelper.setValue(true, false);
                            helpers.setValue(secondReasonRework, false);
                        }
                    });
                    footer.setCheckSecondDocument(
                        true,
                        String(secondReasonRework),
                        rightCategory,
                        currentStepData?.compareTo.documentId
                    );
                }
            }
        }
    }, [secondReasonRework]);

    useUpdateEffect(() => {
        if (isLeftPacket) {
            const { current: formik } = formikRef;
            const { current: footer } = innerFooterRef;
            if (formik && footer) {
                if (firstReasonRework) {
                    currentOriginalData?.forEach((e, index) => {
                        if (
                            index !== currentIndexFormikInput &&
                            ((typeof e.compareTo.documentId === 'undefined' &&
                                e.compareTo.category === leftCategory) ||
                                (typeof currentStepData?.compareWith
                                    .documentId !== 'undefined' &&
                                    e.compareTo.documentId ===
                                        currentStepData?.compareWith
                                            .documentId &&
                                    ((currentStepData?.compareWith
                                        .documentId === null &&
                                        e.compareTo.category ===
                                            leftCategory) ||
                                        currentStepData?.compareWith
                                            .documentId !== null)))
                        ) {
                            const helpers = formik.getFieldHelpers(
                                `reviewedSteps[${index}].secondReason`
                            );
                            const fieldHelper = formik.getFieldHelpers(
                                `reviewedSteps[${index}].isSecondDocument`
                            );
                            fieldHelper.setValue(true, false);
                            helpers.setValue(firstReasonRework, false);
                        }
                        if (
                            index !== currentIndexFormikInput &&
                            ((typeof e.compareWith.documentId === 'undefined' &&
                                e.compareWith.category === leftCategory) ||
                                (typeof currentStepData?.compareWith
                                    .documentId !== 'undefined' &&
                                    e.compareWith.documentId ===
                                        currentStepData?.compareWith
                                            .documentId &&
                                    ((currentStepData?.compareWith
                                        .documentId === null &&
                                        e.compareWith.category ===
                                            leftCategory) ||
                                        currentStepData?.compareWith
                                            .documentId !== null)))
                        ) {
                            const helpers = formik.getFieldHelpers(
                                `reviewedSteps[${index}].firstReason`
                            );
                            const fieldHelper = formik.getFieldHelpers(
                                `reviewedSteps[${index}].isFirstDocument`
                            );
                            fieldHelper.setValue(true, false);
                            helpers.setValue(firstReasonRework, false);
                        }
                    });
                    footer.setCheckFirstDocument(
                        true,
                        String(firstReasonRework),
                        leftCategory,
                        currentStepData?.compareWith.documentId
                    );
                }
            }
        }
    }, [firstReasonRework]);

    return (
        <Formik
            innerRef={formikRef}
            validationSchema={SendToReworkScheme}
            initialValues={{
                reviewedSteps: arraySteps,
            }}
            enableReinitialize
            onSubmit={() => {
                // console.log('');
            }}
        >
            <>
                <HeadTag title="Document Viewer for Reviewer" />
                <DocumentMeta.Provider value={initialDocumentMeta}>
                    <FooterWrapper
                        ref={innerFooterWrapperRef}
                        Footer={Footer}
                        innerRef={innerFooterRef}
                        onClickStepFooter={handleClickStepOnFooter}
                        onChangedStepFooter={handleChangedStepOnFooter}
                        onChangedCategoryFooter={handleChangedCategoryOnFooter}
                        mainContainerRef={innerMainRef}
                    >
                        {viewerLoading ? (
                            <Box className="align-loader">
                                <CircularProgress
                                    className="mr-2 margin-loader"
                                    size={54}
                                    color="inherit"
                                    title="progress loading"
                                />
                            </Box>
                        ) : (
                            <DocumentViewer
                                networkErrorLeft={isNetworkErrorLeft}
                                networkErrorRight={isNetworkErrorRight}
                                delayedLeft={progressionLeftSasToken}
                                delayedRight={progressionRightSasToken}
                                stack={currentStepData?.stack}
                                type={currentType}
                                ref={documentViewerRef}
                                step={currentStepData?.sequence}
                                result={currentStepData?.result}
                                isHybrid={
                                    defaultState &&
                                    typeof defaultState.packetType !==
                                        'undefined' &&
                                    defaultState.packetType === 'hybrid'
                                }
                                isLeftOriginalMissing={isMissingLeftDocument}
                                isRightOriginalMissing={isMissingRightDocument}
                                footerCategory={footerCategory}
                                isLeftActive={isLeftPacket}
                                isRightActive={isRightPacket}
                                leftDocumentUri={leftDocument}
                                leftCategory={leftCategory}
                                leftFilename={leftFilename}
                                leftVerticesData={leftVerticesData}
                                rightDocumentUri={rightDocument}
                                rightCategory={rightCategory}
                                rightFilename={rightFilename}
                                rightVerticesData={rightVerticesData}
                                setLeftVerticesData={actionOnDrawLeftDocument}
                                setRightVerticesData={actionOnDrawRightDocument}
                                handleHiddenFooter={handleHiddenFooter}
                                handleShowFooter={handleShowFooter}
                                leftCompareValue={leftCompareValue}
                                rightCompareValue={rightCompareValue}
                                leftDrawn={leftDrawn}
                                rightDrawn={rightDrawn}
                                emptyLeftVerticesData={
                                    actionOnEmptyDrawLeftDocument
                                }
                                emptyRightVerticesData={
                                    actionOnEmptyDrawRightDocument
                                }
                                handleNetworkError={() =>
                                    setCurrentStepPDFNetworkError(true)
                                }
                            />
                        )}
                    </FooterWrapper>
                </DocumentMeta.Provider>
            </>
        </Formik>
    );
};

export default SolicitorDocumentPage;
